/**
 * Спільні контракти запиту й тендера на фронті (`SPEC-022` §3.4, `SPEC-020`
 * §10.1). Конкретного ресурсу компоненти секції не знають: адреси, ключі й
 * тексти передає власник (`RequestContainer`, далі — `TenderContainer`).
 */

/** Дієслівні переходи (`TransportRequestTransition` беку): ключ `_permissions` і суфікс маршруту. */
export type TransportRequestTransition = 'lose' | 'return_to_work' | 'convert'

/**
 * `_permissions` запису з `?permissions=true`: Rule уже врахував і роль, і
 * дозволеність переходу з поточного стану, тож кнопки показуються лише за ними.
 */
export interface TransportRequestPermissions {
    read?: boolean
    update?: boolean
    delete?: boolean
    lose?: boolean
    return_to_work?: boolean
    convert?: boolean
}

/** Рядок довідника статусів (`status_list:short:read`). */
export interface TransportRequestStatusRef {
    '@id'?: string
    uuid?: string
    code?: string
    title?: string
    color?: string
}

/** Вкладене посилання з назвою (клієнт, країна, тип авто, причина). */
export interface TransportRequestRelationRef {
    '@id'?: string
    uuid?: string
    title?: string
    code?: string
}

/** Значення картки так, як їх тримає ядрова форма. */
export interface TransportRequestValues {
    uuid?: string
    '@id'?: string
    id?: number
    title?: string
    status?: TransportRequestStatusRef | null
    client?: TransportRequestRelationRef | null
    _permissions?: TransportRequestPermissions
    [key: string]: unknown
}

/** Те, що компоненти секції беруть із ядрового `formContext`. */
export interface TransportRequestFormContextLike {
    isEditable?: boolean
    values?: TransportRequestValues
    getError?: (path: string) => unknown
    setInitialValue?: (path: string, value: unknown) => void
    setInitialValues?: (values: Record<string, unknown>) => void
    setStorage?: (key: string, value: unknown) => void
}

/** Те, що кнопки переходів беруть із ядрового `crudContext` картки. */
export interface TransportRequestCrudContextLike {
    resource?: { key?: string; restUrl?: string; fields?: Record<string, unknown> }
    formContext?: TransportRequestFormContextLike
    actions?: { reloadData?: () => unknown }
    listCrudContext?: { listContext?: { actions?: { load?: (force?: boolean) => unknown } } }
}

/**
 * Налаштування кнопок переходів одного ресурсу (запиту чи тендера).
 *
 * `convert` — Hook `ApplicationContainer` (`SPEC-020` §2.3 п. 3): відкриває
 * створення заявки з полями запису й викликає `done()` після збереження.
 * Поки контейнера заявок немає (021-Н4), власник його не передає — і кнопки
 * «Конвертація в заявку» немає навіть за `_permissions.convert`.
 */
export interface TransportRequestActionsConfig {
    restUrl: string
    /** Ключ ресурсу довідника причин програшу (`Setting.ListRequestLossReason`) */
    lossReasonResource: string
    /** Поле «Коментар» у модалці програшу (заявка, Р21.4) */
    withComment?: boolean
    texts: {
        /** «Запит успішно програно.» */
        loseSuccess: string
        /** «Запит успішно повернуто в роботу.» */
        returnSuccess: string
    }
    convert?: (record: TransportRequestValues, done: () => void) => void
}

/** Тексти, спільні для запиту, тендера й заявки (`SPEC-022` §8.4, `uk.json`). */
export const TRANSPORT_REQUEST_TEXTS = {
    successTitle: 'Успіх',
    errorTitle: 'Помилка',
    contactAdmin: 'Зверніться до адміністратора системи.',
    loseModalTitle: 'Вкажіть причину програшу',
    comment: 'Коментар',
    save: 'Зберегти',
    close: 'Закрити',
    lose: 'Програти',
    returnToWork: 'Повернути в роботу',
    convert: 'Конвертація в заявку',
    offersCount: 'Кількість пропозицій',
} as const
